import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import {
  CircularLoader,
  PencilIcon,
  RefreshIcon,
  TrashFilledIcon,
  Typography,
  useHapticFeedback,
} from '@blade/ui-kit'
import { WalletColorDrawer } from '@root/features/wallet'
import { walletDeletedEvent } from '@root/features/wallet/delete-wallet'
import { DeleteWalletDrawer } from '@root/features/wallet/delete-wallet/ui/mobile'
import { WalletExportDrawer } from '@root/features/wallet/export-wallet'
import { reatomRefreshWallet } from '@root/features/wallet/refresh-wallet'
import {
  ChevronRightSmallIcon,
  DatabaseExportIcon,
  Drawer,
  PaletteIcon,
} from '@root/shared/ui-kit'
import { WalletRenameDrawer } from '@root/widgets/wallet'

import type { WalletModel } from '@root/entities/wallet/atoms'
import type { DrawerProps } from '@root/shared/ui-kit'

const ICON_SIZE = 16

type WalletOperationsDrawerProps = Pick<
  DrawerProps,
  'open' | 'onOpenChange'
> & { wallet: WalletModel }

export const WalletOperationsDrawer: React.FC<WalletOperationsDrawerProps> = ({
  open,
  onOpenChange,
  wallet,
}) => {
  const { t } = useTranslation()
  const haptics = useHapticFeedback()
  const [isColorDrawerOpened, setIsColorDrawerOpened] = useState(false)
  const [isRenameDrawerOpened, setIsRenameDrawerOpened] = useState(false)
  const [isExportDrawerOpened, setIsExportDrawerOpened] = useState(false)

  const [isDeleteDrawerOpened, setIsDeleteDrawerOpened] = useState(false)

  const refreshRequestAtom = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomRefreshWallet({ walletId: wallet.id }),
    [wallet.id],
  )
  const [refreshRequestState] = useAtom(refreshRequestAtom)
  const refreshWallet = useAction(refreshRequestAtom.execute)

  useEffect(() => {
    return walletDeletedEvent.onCall((ctx, _, [{ walletId }]) => {
      if (walletId === wallet.id) {
        onOpenChange?.(false)
      }
    })
  }, [onOpenChange, wallet.id])

  return (
    <Drawer
      title={t('wallet.operations')}
      open={open}
      onOpenChange={onOpenChange}
    >
      <Root>
        <ItemRoot
          onClick={() => {
            haptics.impactOccurred('light')
            setIsRenameDrawerOpened(true)
          }}
        >
          <PencilIcon size={ICON_SIZE} />
          <ItemText>{t('wallet.rename_wallet')}</ItemText>
          <ChevronIcon />
        </ItemRoot>
        <ItemRoot
          onClick={() => {
            haptics.impactOccurred('light')
            setIsColorDrawerOpened(true)
          }}
        >
          <PaletteIcon size={ICON_SIZE} />
          <ItemText>{t('wallet.change_wallet_color')}</ItemText>
          <ChevronIcon />
        </ItemRoot>
        <ItemRoot
          onClick={() => {
            haptics.impactOccurred('light')
            setIsExportDrawerOpened(true)
          }}
        >
          <DatabaseExportIcon size={ICON_SIZE} />
          <ItemText>{t('wallet.export_wallet')}</ItemText>
          <ChevronIcon />
        </ItemRoot>
        <ItemRoot
          onClick={() => {
            haptics.impactOccurred('light')
            refreshWallet()
          }}
          $disabled={AsyncStateN.isLoading(refreshRequestState)}
        >
          {AsyncStateN.isLoading(refreshRequestState) ? (
            <CircularLoader size={ICON_SIZE} />
          ) : (
            <RefreshIcon size={ICON_SIZE} />
          )}
          <ItemText>{t('wallet.refresh-wallet')}</ItemText>
        </ItemRoot>
        <ItemRoot
          onClick={() => {
            haptics.impactOccurred('light')
            setIsDeleteDrawerOpened(true)
          }}
        >
          <TrashFilledIcon
            size={ICON_SIZE}
            style={{
              color: 'var(--red-9)',
            }}
          />
          <ItemText
            css={css`
              color: var(--red-9);
            `}
          >
            {t('wallet.delete_wallet')}
          </ItemText>
          <ChevronIcon />
        </ItemRoot>
      </Root>
      <WalletColorDrawer
        wallet={wallet}
        open={isColorDrawerOpened}
        onOpenChange={setIsColorDrawerOpened}
      />
      <WalletRenameDrawer
        wallet={wallet}
        open={isRenameDrawerOpened}
        onOpenChange={setIsRenameDrawerOpened}
      />
      <WalletExportDrawer
        wallet={wallet}
        open={isExportDrawerOpened}
        onOpenChange={setIsExportDrawerOpened}
      />

      <DeleteWalletDrawer
        wallet={wallet}
        open={isDeleteDrawerOpened}
        onOpenChange={setIsDeleteDrawerOpened}
      />
    </Drawer>
  )
}

const Root = styled.div`
  display: grid;
  gap: 8px;
  padding-inline: 12px;
  padding-block: 12px calc(var(--safe-area-inset-bottom) + 12px);
`

const ItemRoot = styled.div<{ $disabled?: boolean }>`
  display: flex;
  height: 40px;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  display: grid;
  padding-inline: 16px;
  grid-template-columns: auto 1fr auto;
  align-items: center;

  transition: all 0.15s linear;
  border-radius: 8px;

  ${({ $disabled }) => {
    if ($disabled) {
      return css`
        background: rgba(255, 255, 255, 0.01);
      `
    }

    return css`
      background: var(--backgroundA-2);
      &:hover {
        background: var(--backgroundA-3);
      }
    `
  }}
`

const ItemText = styled(Typography).attrs({ variant: 'body4-medium' })`
  padding-inline: 8px;
  color: var(--text-11);
`

const ChevronIcon = styled(ChevronRightSmallIcon)`
  color: var(--text-10);
`
